
import React, { useEffect, useRef, useState } from "react";
import "./style.css";

// Assets

import GirlClap from "../../assets/images/GiF/girl-clap.gif";
import Sparkle from "../../assets/images/GiF/sparkel.gif";
import GirlChar from "../../assets/images/girl-char.png";
import Bubble from "../../assets/images/bubble.png";
import PointingGif from "../../assets/images/GiF/pointing-hand.gif";

// Utilities
import parse from "html-react-parser";
import {
  playSpeech,
  playSpeechTTS,
  stopSpeechTTS,
} from "../../utils/playSpeech";
import { playSpeechKokoro } from "../../utils/playSpeechKokoro";
import { stopSpeechKokoro } from "../../utils/playSpeechKokoro";

// Components / Pages
import { stopLayeredSVGAudio } from "../../pages/categories/LayeredSVG";

// Context
import { useAuth } from "../../context/AuthContext";

// Audio files
import ClickScreenAUD from "../../assets/audios/Click the screen.wav";
import IncompleteAUD from "../../assets/audios/You have complete.wav";

/* ============================================================
   Dialogue Wrapper
   ------------------------------------------------------------
   Common wrapper used for dialogue messages that display the
   girl character, speech bubble, and pointing-hand animation.
   ============================================================ */

function DialogueWrapper({ children, audioSRC }) {
  const [showHand, setShowHand] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHand(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="layout-wrapper" onClick={stopSpeechKokoro}>
      <div className="gir-char-div">
        <img
          src={GirlChar}
          alt="Girl Character"
          className="girl-char"
        />
      </div>

      <div className="bubble bubble-bottom-right">{children}</div>

      {audioSRC && <audio src={audioSRC} autoPlay />}

      <div className="hand-cursor-small">
        <img src={PointingGif} alt="Hand Cursor" />
      </div>

      {/*
        {showHand && (
          <div className="hand-cursor-small">
            <img src={PointingGif} alt="Hand Cursor" />
          </div>
        )}
      */}
    </div>
  );
}

/* ============================================================
   Great Job Kiddo
   ------------------------------------------------------------
   Displays the "Have fun Learning, KIDDO!" message.
   ============================================================ */

export function GreatJobKiddo({ isActive }) {
  const { setCommonError } = useAuth();

  useEffect(() => {
    stopLayeredSVGAudio();

    if (isActive) {
      playSpeech("Have fun Learning, KIDDO!");
    }

    // if (isActive) {
    //   playSpeechTTS("Have fun Learning, KIDDO!", setCommonError);
    // }
  }, [isActive]);

  return (
    <div className="layout-wrapper">
      <img
        src={GirlChar}
        alt="Girl Character"
        className="click-girl-char"
      />

      <div className="bubble">
        <img
          src={Bubble}
          alt="Speech Bubble"
          className="click-bubble-bg"
        />

        <p className="click-text">Have fun Learning, KIDDO!</p>
      </div>

      <div className="hand-cursor-overlay">
        <img src={PointingGif} alt="Hand Cursor" />
      </div>
    </div>
  );
}

/* ============================================================
   Click To Listen
   ------------------------------------------------------------
   Displays the initial instruction asking the child to click
   the screen to start learning.
   ============================================================ */

export function ClickToListen({ isActive }) {
  const { setCommonError, selectedLang } = useAuth();

  // console.log(
  //   "Selected Language in ClickToListen: guidance",
  //   selectedLang
  // );

  useEffect(() => {
    stopLayeredSVGAudio();
    stopSpeechKokoro();

    if (isActive) {
      playSpeechKokoro("Click the screen to start learning!");
    }

    // if (isActive) {
    //   playSpeechTTS("Click me, to Start Learning!", setCommonError);
    // }
  }, [isActive]);

  return (
    <div
      className="layout-wrapper-left"
      onClick={stopSpeechKokoro}
    >
      <div className="click-gir-char-div">
        <img
          src={GirlChar}
          alt="Girl Character"
          className="click-girl-char"
        />
      </div>

      <audio src={null} autoPlay />

      <div
        className={`bubble-left ${
          selectedLang?.code !== "en" ? "j-lang" : ""
        }`}
      >
        Click the screen to start learning!
      </div>

      <div className="hand-cursor-small-click">
        <img src={PointingGif} alt="Hand Cursor" />
      </div>
    </div>
  );
}

/* ============================================================
   Great Kiddo
   ------------------------------------------------------------
   Displays a simple "Great job, kiddo!" message.
   ============================================================ */

export function GreatKiddo({ isActive }) {
  const { setCommonError } = useAuth();

  useEffect(() => {
    stopLayeredSVGAudio();

    if (isActive) {
      playSpeech("Great job, kiddo!");
    }

    // if (isActive) {
    //   playSpeechTTS("Great job, kiddo!", setCommonError);
    // }
  }, [isActive]);

  return (
    <div className="layout-wrapper">
      <img
        src={GirlChar}
        alt="Girl Character"
        className="click-girl-char"
      />

      <div className="bubble">
        <img
          src={Bubble}
          alt="Speech Bubble"
          className="click-bubble-bg"
        />

        <p className="click-text">Great job, kiddo!</p>
      </div>
    </div>
  );
}

/* ============================================================
   Intro Message
   ------------------------------------------------------------
   Displays the introduction message for a subtopic and plays
   the corresponding audio / Kokoro speech when active.
   ============================================================ */

export function IntroMessage({
  description,
  subtopicName,
  isActive,
  audioFile,
}) {
  const { setCommonError } = useAuth();
  const audioRef = useRef(null);

  // Remove wrapping <p> tags from the description so that the
  // description can be displayed and used in the speech text.
  const cleanDesc =
    typeof description === "string"
      ? description
          .replaceAll("<p>", "")
          .replaceAll("</p>", "")
      : "";

  useEffect(() => {
    stopLayeredSVGAudio();

    // Only play the audio when the component is active and
    // an audio file has been provided.
    if (isActive && audioFile && audioRef.current) {
      // Stop any currently playing audio before starting.
      audioRef.current.pause();
      audioRef.current.currentTime = 0;

      audioRef.current.play().catch((err) => {
        console.error("Audio play failed:", err);
        setCommonError(err.message);
      });
    }

    if (isActive) {
      stopSpeechKokoro();

      playSpeechKokoro(
        `We are going to learn about ${subtopicName} by ${cleanDesc}. Let's learn, shall we?`
      );
    }

    /*
      playSpeechTTS(
        `We are going to learn about ${subtopicName} by ${cleanDesc}. Let's learn, shall we?`,
        setCommonError
      );
    */

    // Cleanup when the component unmounts or becomes inactive.
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [isActive, audioFile, subtopicName, cleanDesc]);

  return (
    <DialogueWrapper>
      <p>
        We are going to learn about <br />

        <span className="topic-title">{subtopicName}</span> by{" "}
        {parse(cleanDesc)}.
      </p>

      {/*
        {audioFile && <audio ref={audioRef} src={audioFile} />}
      */}

      <p>Let’s learn, shall we?</p>
    </DialogueWrapper>
  );
}

/* ============================================================
   Next Message
   ------------------------------------------------------------
   Displays a transition message before moving to the next
   subtopic / activity.
   ============================================================ */

export function NextMessage({
  isActive,
  nextDescription,
  audioFile,
}) {
  const { setCommonError } = useAuth();
  const audioRef = useRef(null);

  const cleanDesc =
    typeof nextDescription === "string"
      ? nextDescription
          .replaceAll("<p>", "")
          .replaceAll("</p>", "")
      : "";

  useEffect(() => {
    stopLayeredSVGAudio();

    // Play the provided audio file when the component is active.
    if (isActive && audioFile && audioRef.current) {
      // Stop any currently playing audio before starting.
      audioRef.current.pause();
      audioRef.current.currentTime = 0;

      audioRef.current.play().catch((err) => {
        console.error("Audio play failed:", err);
        setCommonError(err.message);
      });
    }

    // Stop any previously running Kokoro speech.
    stopSpeechKokoro();

    // Play the transition message when active.
    if (isActive) {
      playSpeechKokoro(
        `Great, Let’s move to "${cleanDesc}"!`
      );
    }

    // if (isActive) {
    //   playSpeechTTS(
    //     `Great, Let’s move to "${cleanDesc}"`,
    //     setCommonError
    //   );
    // }

    // Cleanup when the component unmounts or becomes inactive.
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [isActive, audioFile, cleanDesc]);

  return (
    <div
      className="layout-wrapper"
      onClick={stopSpeechKokoro}
    >
      <div className="sparkle-wrapper">
        <div className="gir-char-div-gif">
          <img
            src={GirlClap}
            alt="Girl Character"
            className="girl-char"
          />
        </div>

        <div>
          <img
            src={Sparkle}
            alt="Sparkles"
            className="sparkles"
          />
        </div>

        {/*
          {audioFile && <audio ref={audioRef} src={audioFile} />}
        */}
      </div>

      <div className="bubble bubble-bottom-right-gif">
        Great, Let’s move to "{cleanDesc}"
      </div>
    </div>
  );
}

/* ============================================================
   Incomplete Message
   ------------------------------------------------------------
   Displays a message when the current level has not been
   completed and the user attempts to move forward.
   ============================================================ */

export function IncompleteMessage({
  isActive,
  message = "You haven’t completed this level yet. Please finish it to move on to the next one!",
}) {
  const { setCommonError } = useAuth();

  useEffect(() => {
    stopLayeredSVGAudio();

    if (isActive) {
      stopSpeechKokoro();
      playSpeechKokoro(message);
    }
  }, [isActive]);

  return (
    <DialogueWrapper audioSRC={null}>
      <p>{message}</p>
    </DialogueWrapper>
  );
}
